import { Component, OnInit, HostListener, Inject, PLATFORM_ID, OnDestroy, ViewEncapsulation, AfterViewInit, ElementRef, Output, EventEmitter, Input, TransferState } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, filter, Subscription, take, takeWhile, tap } from 'rxjs';
import { MktDynamicOfferService } from 'src/app/core/services/mkt-dynamic-offer.service';
import { MktEnvironmentService } from 'src/app/core/services/mkt-environment.service';
import { MktContentStorageService } from 'src/app/core/services/mkt-content-storage.service';
import { MktSiteCatalystService } from 'src/app/core/services/mkt-site-catalyst.service';
import { ScrollFocusService } from 'src/app/common/services/scroll-focus.service';
import { MktCardRetrievalService } from 'src/app/core/services';
import { HeaderSectionBoxComponent } from './header-section-box/header-section-box.component';
import { StickyHeaderComponent } from './sticky-header/sticky-header.component';
import { MktCommonModule } from 'src/app/common/mkt-common.module';

@Component({
  selector: 'app-header-section',
  templateUrl: './header-section.component.html',
  styleUrls: ['./header-section.component.scss'],
  standalone: true,
  imports:[MktCommonModule,
        StickyHeaderComponent,
        HeaderSectionBoxComponent,
  ],
  encapsulation: ViewEncapsulation.None
})
export class HeaderSectionComponent implements OnInit, OnDestroy {

  cardImageSize = 'md';
  cardDetails: any = null;
  reviewContent: any = null;
  headerContent: any = null;
  hrefTarget = '_blank';
  prodName: string;
  imageBasePath: string = '';
  cardApplyNowBtnLink: string;
  routeSub: Subscription = new Subscription();
  private dynamicOfferSub: Subscription = new Subscription();
  flowId: string = '';

  @Output() anchorLinkClicked = new EventEmitter<any>();
  @Output() applyNowClicked = new EventEmitter<any>();
  @Output() callToApplyClicked = new EventEmitter<any>();
  @Output() taggingEmit = new EventEmitter<any>();
  @Output() pricingInfoClicked = new EventEmitter<any>();
  @Output() additionalInfoClicked = new EventEmitter<any>();

  constructor(@Inject(PLATFORM_ID) private platformId: string,
    private _mktContentStorageService: MktContentStorageService,
    private mktDynamicOfferService: MktDynamicOfferService,
    private mktEnvironmentService: MktEnvironmentService,
    private mktSiteCatalystService: MktSiteCatalystService,
    public state: TransferState,
    private route: ActivatedRoute,
    private scrollFocusService: ScrollFocusService,
    private cardRetrievalService: MktCardRetrievalService) {
    this.imageBasePath = this.mktEnvironmentService.getEnvironmentVar('cardsmkrHostName');
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.setCardSize();
    }
    this.getContent();
  }

  ngOnDestroy() {
    this.dynamicOfferSub.unsubscribe();
    this.routeSub.unsubscribe();
  }


  private getContent() {
    this.routeSub = combineLatest([
      this.route.params,
      this.route.queryParams
    ]).subscribe(params => {
      const cardName = params[0]['cardName'].replace(/\./g, '');
      this.flowId = params[1]['flowId'] || '';

      // Try to get card pricing details
      const mainPdpContentObject = this._mktContentStorageService.get('Product_Detail_Pages', 'copy', cardName, undefined);
      if (!mainPdpContentObject) {
        return;
      }
      this.cardDetails = JSON.parse(mainPdpContentObject['TEXT']);
      const pidPT = this.cardDetails?.pid + (this.cardDetails?.productType ? '_' + this.cardDetails?.productType : '');
      this.cardDetails['pidPT'] = pidPT;

      let headerContent = JSON.parse(mainPdpContentObject['headerSection']);
      if(headerContent && Array.isArray(headerContent)){
        headerContent.forEach(t=>{
          if(t?.selectedTemplate){
            headerContent = t;
          }
        })
      }
      this.headerContent = {
        ...headerContent,
        offerImagePath: `${this.imageBasePath}${headerContent.offerImagePath || 'NerdWalletBestSavings2018.png'}`,
        iconPath: `${this.imageBasePath}${headerContent.iconPath}`,
        backgroundColor: headerContent.backgroundColor || '#00843d',
        textColor: headerContent.textColor || '#FFFFFF',
        offerType: headerContent.offerType || 'text',
      };
      this.reviewContent = JSON.parse(mainPdpContentObject['reviewSection']);
      const siteCatalyst = JSON.parse(mainPdpContentObject['siteCatalyst']);
      this.prodName = siteCatalyst['pageLoadTag']['prodName'];

      //  get the dynamic url once the contet is load
      this.dynamicOfferSub = this.mktDynamicOfferService.getApplyNowContent().subscribe((content) => {
        this.cardApplyNowBtnLink = content.applyNowUrl;
      });
    });
    
    if (isPlatformBrowser(this.platformId)) {
      this.preloadImage();
    }
  }

  @HostListener('window:resize', [])
  setCardSize() {
    this.cardImageSize = (window.innerWidth < 768) ? 'sm' : 'md';
  }

  scroll(sectionId: string) {
    this.scrollFocusService.scrollFocus({},sectionId,'pdp');
  }

  scrollToReviewSection(sectionId: string) {
    this.mktSiteCatalystService.callSC('ratingsAndReviewsClick', { 'ratingAndReviewDetail': this.prodName });
    this.scroll(sectionId);
    this.anchorLinkClicked.emit('Reviews');
  }

  isMobileWithoutRating() { return (this.reviewContent.reviewActive === false && this.cardDetails.customerSupportText && window.innerWidth < 768); }

  anchorLink(value){
    if(document.getElementById('Pricing').innerText){
      const output = value.replace(/\*/g, '');
      this.anchorLinkClicked.emit(output);
    } else {
      this.anchorLinkClicked.emit(value);
    }
  }

  applyNow(value){
    this.applyNowClicked.emit(value);
  }

  pricingInfo(value){
    this.pricingInfoClicked.emit(value);
  }
  
  additionalInfo(event){
    this.additionalInfoClicked.emit(event);
  }

  callToApplyModalOpen(value) {
    this.callToApplyClicked.emit(value);
  }

  private preloadImage() {
    const imagePrefix = this.mktEnvironmentService.getEnvironmentVar('cardsmkrHostName');
    const cardName = this.cardRetrievalService.getCardByPid(this.cardDetails?.pid);
    const script = document.createElement('link');
    script.href = imagePrefix + '/CRD/images/' + cardName?.internalCardName + '/' + cardName?.internalCardName + '_341x215.png';
    script.rel = 'preload';
    script.as = 'image'
    document.getElementsByTagName('head')[0].appendChild(script);
  }
}
